/**
 * The terms of service, in the same plain voice and layout as the privacy page
 * (it reuses that page's styles on purpose: two legal pages that look like
 * siblings read as one promise).
 *
 * DRAFT WORDING, written 2026-10-25 during the California launch review. The
 * structure is what the product needs (age floor, acceptable use, customer
 * data, copyright complaints, automatic renewal, cancellation); the exact
 * legal wording, the liability section and the governing law must be
 * reviewed by counsel before launch. Every statement about how the product
 * behaves must stay true: if billing, retention or upload links change, this
 * page changes in the same commit.
 */

import { Link } from 'react-router';

import { Wordmark } from '../../components/brand/Wordmark';
import { t } from '../../lib/i18n/t';

import '../../styles/screens/privacy.css';

const CONTACT_EMAIL = 'hello@sv-academy.org';
const LAST_UPDATED = '2026-10-25';

export default function Terms() {
  const sections: { title: string; body: string[] }[] = [
    {
      title: t('Who you are agreeing with'),
      body: [
        t('SV Cloud is run by Silicon Valley Innovation Co., Ltd. in Bangkok, Thailand. By creating an account or using SV Cloud you agree to these terms. If you use it for an organisation, you agree for that organisation.'),
      ],
    },
    {
      title: t('Who can use SV Cloud'),
      body: [
        t('You must be at least 13. If you are under 18, you need permission from a parent or guardian, and you may use SV Cloud only through a class or program that gives you access.'),
      ],
    },
    {
      title: t('Your account'),
      body: [
        t('Keep your sign in safe. You are responsible for what happens under your account, including what your coding agent or collaborators do with the access you give them.'),
      ],
    },
    {
      title: t('What you may not do'),
      body: [
        t('Do not use SV Cloud to break the law, to host malware or phishing, to send spam, to attack or overload other systems, to mine cryptocurrency, to store or share material that sexually exploits children, or to share content you do not have the right to share. We may take an app offline or suspend an account that does any of these, and report it where the law requires.'),
      ],
    },
    {
      title: t('Your apps and your users'),
      body: [
        t('Your code, your data and your files stay yours. You let us store, copy and run them only as needed to provide SV Cloud to you.'),
        t('When your app collects information about the people who use it, you decide what it collects and you are responsible for telling them, usually in your own privacy notice. We handle that information only to run your app for you: we keep error and log messages your app writes for 30 days, and we do not sell it or use it for anything else.'),
      ],
    },
    {
      title: t('Upload links and copyright complaints'),
      body: [
        t('If you send someone an upload link, you are responsible for what they upload into your app.'),
        t('If you believe something hosted on SV Cloud infringes your copyright or breaks these terms, write to us with the address of the content, what it infringes, and how to reach you. We will look at it and may remove it.'),
      ],
    },
    {
      title: t('Plans, renewal and cancelling'),
      body: [
        t('New accounts start with one free cycle of the Basic plan. Plans run in monthly cycles. Nothing renews unless you turn on auto-pay on the Account page, where the plan, the price and how to cancel are shown. With auto-pay on, the plan renews automatically at the start of each cycle at the price shown for it, paid from your credits, and we email you a confirmation.'),
        t('Before your free cycle turns into a paid one, and before each renewal, we email you the plan, the price, the date and how to cancel.'),
        t('To cancel, turn auto-pay off on the Account page at any time, in one step. Your plan then ends with its current cycle and nothing more is charged. Charges already made are not refunded, except where the law requires.'),
      ],
    },
    {
      title: t('When we may stop the service'),
      body: [
        t('We may suspend or close an account that breaks these terms, puts others at risk, or leaves bills unpaid. Where we can, we tell you first. You can delete your account at any time from the Account page.'),
      ],
    },
    {
      title: t('No guarantees'),
      body: [
        t('We work hard to keep SV Cloud running, but it is provided as it is. As far as the law allows, we are not responsible for lost profits or lost data, and our total responsibility is limited to what you paid us in the last 12 months. Keep your own copy of anything important.'),
      ],
    },
    {
      title: t('When these terms change'),
      body: [
        t('If we change these terms in a way that matters, we tell you by email or in the dashboard before the change takes effect, and we put the new date at the top.'),
      ],
    },
  ];

  return (
    <div className="pv">
      <div className="pv__lockup">
        <Link className="pv__home" to="/" aria-label={t('SV Cloud')}>
          <Wordmark />
        </Link>
      </div>

      <header className="pv__head">
        <h1 className="pv__title">{t('Terms of service')}</h1>
        <p className="pv__lede">{t('The rules for using SV Cloud, in plain words.')}</p>
        <p className="pv__stamp">
          <span>{t('Last updated')}</span>
          <span className="mono">{LAST_UPDATED}</span>
        </p>
      </header>

      <div className="pv__body">
        {sections.map((section) => (
          <section className="pv__section" key={section.title}>
            <h2 className="pv__section-title">{section.title}</h2>
            {section.body.map((line) => (
              <p className="pv__say" key={line}>
                {line}
              </p>
            ))}
          </section>
        ))}

        <section className="pv__section">
          <h2 className="pv__section-title">{t('Talk to a person')}</h2>
          <p className="pv__say">{t('Questions, abuse reports and copyright complaints all go to the same place.')}</p>
          <p className="pv__contact">
            <a className="link-inline mono" href={`mailto:${CONTACT_EMAIL}`}>
              {CONTACT_EMAIL}
            </a>
          </p>
          <p className="pv__say pv__say--quiet">
            <Link className="link-inline" to="/privacy">
              {t('Privacy policy')}
            </Link>
          </p>
        </section>
      </div>
    </div>
  );
}
